/*
 * Breakpoints — the `bp/*` family (design-system §9 Viewport and breakpoints; FS-3 FR-T7).
 *
 * SOURCE OF TRUTH: forge-et-fe/src/ui/breakpoints.ts
 * forge-et-pwa/src/ui/breakpoints.ts is a byte-identical copy — the two repos share no package
 * linkage (FS-3 spec Q-1). Edit the source, then re-copy; the PWA's `pnpm check:shared` hashes both.
 *
 * Why a module rather than a row in tokens.css: a custom property cannot be used inside a
 * media-query condition (§9 rule 3), so this is the one family the token file cannot carry. It is
 * defined once per repo and everything else imports it — FS-3 AC-6 greps for a raw pixel width inside
 * an @media rule and expects nothing but this module to match.
 *
 * Desktop-first; tablet portrait (768px) is the supported floor. Below it the CRM is not designed,
 * but it still reflows at 320px without horizontal scrolling (WCAG 2.1 SC 1.4.10 — a zoomed-in
 * desktop, not a phone layout; design-system §9). That floor is a PRD commitment
 * rather than a proposal one — the accepted proposal says only "responsive web application" — so
 * raising it back to `laptop` stays a legitimate scope lever (§9).
 */

/** Minimum viewport widths, in CSS px. */
export const bp = {
    /** Tablet portrait — the floor. Below this the CRM only has to reflow (SC 1.4.10), not be designed. */
    tablet: 768,
    /** Tablet landscape / small laptop. */
    laptop: 1024,
    /** The design target — every screen is drawn at this width first. */
    desktop: 1280,
} as const;

export type Breakpoint = keyof typeof bp;

/**
 * The same values as `min-width` media-query conditions, for `matchMedia` and any CSS-in-TS
 * caller. Derived from `bp` rather than retyped, so the two cannot drift.
 */
export const mq = {
    tablet: `(min-width: ${bp.tablet}px)`,
    laptop: `(min-width: ${bp.laptop}px)`,
    desktop: `(min-width: ${bp.desktop}px)`,
} as const satisfies Record<Breakpoint, string>;
